import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, CircularProgress, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { filter, fromPairs, get, map, replace } from 'lodash';
import React, { useMemo } from 'react';

import { prepareCustomerResults } from 'api/customerResults';
import { cancelJob } from 'api/jobs';
import { getUserContextDetails, UserIdentity } from 'components/atoms/UserIdentity';
import {
  isRerunnableJob,
  isSecondaryAnalysisJob,
  Job,
  JobArchiveState,
  JobResponse,
  JobStatus,
  JobType,
} from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { UsersById } from 'interfaces/userCredentials';
import { useAppSelector } from 'redux/hooks';
import { canQueryUsersEndpoint } from 'utils/canQueryUsersEndpoint';
import { formatDateTimeForUser } from 'utils/dateTime';
import { humanize } from 'utils/helpers';
import { normalizeKeysToCamelCase } from 'utils/objectCase';
import { getJobQueryKey } from 'utils/queryHooks/jobs/useJob';
import { jobsQueryKey } from 'utils/queryHooks/jobs/useJobs';
import { useUiSettings } from 'utils/queryHooks/uiConstantsHooks';
import useUsersById from 'utils/queryHooks/useUsersById';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { usePermissions } from 'utils/usePermissions';
import { JobArchiveControl } from './JobArchiveControl';
import { getJobOutcomeSummary } from './jobOutcomeSummary';
import { getJobStatusDisplayLabel, JobStatusChip } from './JobStatusChip';
import { getJobTypeDisplayLabel } from './jobTypeLabels';

const replaceUpdatedJob = (job: Job | undefined, updatedJob: Partial<Job> & Pick<Job, 'id'>): Job | undefined => {
  if (!job || job.id !== updatedJob.id) {
    return job;
  }

  return {
    ...job,
    ...updatedJob,
  } as Job;
};

export const replaceUpdatedJobInJobsResponse = <JobType extends Job = Job>(
  jobsResponse: JobResponse<JobType> | undefined,
  updatedJob: Partial<JobType> & Pick<Job, 'id'>
): JobResponse<JobType> | undefined => {
  if (!jobsResponse) {
    return jobsResponse;
  }

  let didUpdateAnyJob = false;
  const jobs = map(jobsResponse.jobs, (job) => {
    if (job.id !== updatedJob.id) {
      return job;
    }

    didUpdateAnyJob = true;
    return {
      ...job,
      ...updatedJob,
    };
  }) as JobType[];

  if (!didUpdateAnyJob) {
    return jobsResponse;
  }

  return {
    ...jobsResponse,
    jobs,
  };
};

// Fixed width/height of a single action icon slot. Every row action column reserves this same
// slot for every possible action (even when that action isn't applicable to the row) so icons
// never shift position depending on which actions happen to be available for a given row.
const ACTION_SLOT_SIZE = 40;
const ACTION_SLOTS_COUNT = 5;

interface ActionSlotProps {
  children?: React.ReactNode;
  // MUI DataGrid's GridActionsCell clones every returned action element and injects these props
  // (meant for an interactive IconButton-like control). This slot can also be a reserved-but-empty
  // placeholder, so these are accepted here only to keep them from leaking onto the DOM as invalid
  // attributes - the real action buttons still get their own onClick from the closure below.
  onClick?: React.MouseEventHandler;
  tabIndex?: number;
  touchRippleRef?: unknown;
}

const ActionSlot: React.FC<ActionSlotProps> = ({
  children,
  onClick: _onClick,
  tabIndex: _tabIndex,
  touchRippleRef: _touchRippleRef,
  ...rest
}) => (
  <Box
    sx={{
      width: ACTION_SLOT_SIZE,
      height: ACTION_SLOT_SIZE,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    {...rest}
  >
    {children}
  </Box>
);

const renderActionSlot = (key: string, content: React.ReactNode) => <ActionSlot key={key}>{content}</ActionSlot>;

export const useJobColumns = ({
  archiveState = JobArchiveState.Active,
  setCurrentJobModal,
  viewOnly,
  usersById: responseUsersById,
}: {
  archiveState?: JobArchiveState.Active | JobArchiveState.Archived;
  setCurrentJobModal?: (value: { jobType: JobType; jobData: any; additionalParams?: Record<string, any> }) => void;
  viewOnly?: boolean;
  usersById?: UsersById;
}): {
  jobColumns: GridColDef<Job>[];
  isLoading: boolean;
} => {
  const loggedInUserId = useAppSelector((state) => state.auth.profile?.userId);

  const { labId } = useCurrentLabId();

  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const canQueryUsers = canQueryUsersEndpoint(hasPermission);
  const { usersById, getUserNameById, isLoadingUsers } = useUsersById({
    enabled: canQueryUsers && !responseUsersById,
  });
  const canContinueJob = hasPermission(Permission.ContinueJob);
  const canCancelAllUsersJobs = hasPermission(Permission.CancelAllUsersJobs);
  const canCancelOwnJobs = hasPermission(Permission.CancelOwnJobs);
  const hasCancelJobPermission = canCancelAllUsersJobs || canCancelOwnJobs;

  const { uiSettings, isLoadingUiSettings } = useUiSettings();

  const prepareCustomerResultsMutation = useMutationWithErrorSnackbar({
    mutationFn: prepareCustomerResults,
    mutationDescription: 'create customer results',
  });

  const queryClient = useQueryClient();

  const cancelJobMutation = useMutationWithErrorSnackbar({
    onMutate: async (jobId) => {
      await queryClient.cancelQueries({ queryKey: [jobsQueryKey] });
      await queryClient.cancelQueries({ queryKey: getJobQueryKey(jobId) });

      const previousJobsQueries = queryClient.getQueriesData<JobResponse<Job>>({
        queryKey: [jobsQueryKey],
      });
      const previousJob = queryClient.getQueryData<Job>(getJobQueryKey(jobId));
      const optimisticCancelledJob = {
        id: jobId,
        status: JobStatus.Cancelled,
      } satisfies Partial<Job> & Pick<Job, 'id'>;

      queryClient.setQueriesData<JobResponse<Job>>({ queryKey: [jobsQueryKey] }, (currentJobsResponse) =>
        replaceUpdatedJobInJobsResponse(currentJobsResponse, optimisticCancelledJob)
      );
      queryClient.setQueryData<Job | undefined>(getJobQueryKey(jobId), (currentJob) =>
        replaceUpdatedJob(currentJob, optimisticCancelledJob)
      );

      return {
        previousJobsQueries,
        previousJob,
      };
    },
    onError: (_error, jobId, context) => {
      context?.previousJobsQueries.forEach(([queryKey, previousJobsResponse]) => {
        queryClient.setQueryData(queryKey, previousJobsResponse);
      });
      queryClient.setQueryData(getJobQueryKey(jobId), context?.previousJob);
    },
    onSuccess: (updatedJob, jobId) => {
      queryClient.setQueriesData<JobResponse<Job>>({ queryKey: [jobsQueryKey] }, (currentJobsResponse) =>
        replaceUpdatedJobInJobsResponse(currentJobsResponse, updatedJob)
      );
      queryClient.setQueryData<Job | undefined>(getJobQueryKey(jobId), (currentJob) =>
        replaceUpdatedJob(currentJob, updatedJob)
      );
    },
    onSettled: (_updatedJob, _error, jobId) => {
      void queryClient.invalidateQueries({ queryKey: [jobsQueryKey] });
      void queryClient.invalidateQueries({ queryKey: getJobQueryKey(jobId) });
    },
    mutationFn: cancelJob,
    mutationDescription: 'cancel job',
  });

  const jobColumns = useMemo((): GridColDef<Job>[] => {
    const jobStatusToDisplayText = fromPairs(
      map(uiSettings?.enumDisplayNames?.['jobStatus'], ({ value, label }) => [value, label]) || []
    );
    const requestSenderToDisplayText = fromPairs(
      map(uiSettings?.enumDisplayNames?.['requestSender'], ({ value, label }) => [value, label]) || []
    );
    const getDisplayedUserName = (userId?: string) => {
      if (!userId) {
        return userId;
      }

      if (responseUsersById) {
        const user = getUserContextDetails(responseUsersById, userId);
        return user?.name || user?.primaryEmail || userId;
      }

      const matchedUser = usersById[userId] || usersById[replace(userId, 'auth0|', '')];
      return matchedUser ? getUserNameById(userId) : userId;
    };

    const commonColumnSettings: Partial<GridColDef<Job>> = { filterable: false };
    const getJobTypeLabel = (row: Job) => {
      if (row.type !== JobType.PostProcessingAndCalculateFeatures) {
        return getJobTypeDisplayLabel(row.type);
      }

      return isSecondaryAnalysisJob(row) ? 'Secondary Analysis' : 'Calculate Features';
    };
    const getJobStatusLabel = (status?: string) => getJobStatusDisplayLabel(status, jobStatusToDisplayText);

    return [
      {
        ...commonColumnSettings,
        field: 'id',
        headerName: 'Job ID',
        flex: 1.2,
        minWidth: 210,
        renderCell: ({ value }) =>
          value ? (
            <Typography
              variant="body2"
              noWrap
              title={value}
              sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', overflow: 'hidden' }}
            >
              {value}
            </Typography>
          ) : null,
      },
      {
        ...commonColumnSettings,
        field: 'type',
        headerName: 'Job Type',
        flex: 1,
        minWidth: 190,
        valueFormatter: (_, row) => getJobTypeLabel(row),
        sortComparator: (_v1, _v2, cellParams1, cellParams2) => {
          const row1: Job = cellParams1.api.getRow(cellParams1.id);
          const row2: Job = cellParams2.api.getRow(cellParams2.id);
          return getJobTypeLabel(row1).localeCompare(getJobTypeLabel(row2));
        },
      },
      {
        ...commonColumnSettings,
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        renderCell: ({ value }) => (
          <Box sx={{ width: '100%', minWidth: 0, height: '100%', display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" fontWeight={600} noWrap title={value || ''}>
              {value}
            </Typography>
          </Box>
        ),
      },
      {
        ...commonColumnSettings,
        field: 'description',
        headerName: 'Description',
        flex: 1.5,
        minWidth: 260,
        renderCell: ({ value }) => (
          <Box sx={{ width: '100%', minWidth: 0, height: '100%', display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" noWrap title={value || ''}>
              {value}
            </Typography>
          </Box>
        ),
      },
      {
        ...commonColumnSettings,
        field: 'status',
        headerName: 'Status',
        width: 260,
        valueFormatter: (value, row) => {
          const summary = getJobOutcomeSummary(row);
          return summary.displayLabel || getJobStatusLabel(summary.displayStatus || value);
        },
        renderCell: ({ value, row }) => {
          const summary = getJobOutcomeSummary(row);
          const displayStatus = summary.displayStatus || value;
          return (
            <Stack spacing={0.25} justifyContent="center" sx={{ height: '100%', minWidth: 0 }}>
              <JobStatusChip status={displayStatus} label={summary.displayLabel || getJobStatusLabel(displayStatus)} />
              {summary.details ? (
                <Typography variant="caption" color="text.secondary" noWrap title={summary.details}>
                  {summary.details}
                </Typography>
              ) : null}
            </Stack>
          );
        },
      },
      {
        ...commonColumnSettings,
        field: 'startedAt',
        headerName: 'Started At',
        width: 180,
        valueFormatter: (value) => formatDateTimeForUser(value),
      },
      ...(archiveState === JobArchiveState.Archived
        ? [
            {
              ...commonColumnSettings,
              field: 'deletedAt',
              headerName: 'Archived At',
              width: 180,
              valueFormatter: (value: string | null | undefined) => (value ? formatDateTimeForUser(value) : ''),
            },
            {
              ...commonColumnSettings,
              field: 'deletedBy',
              headerName: 'Archived By',
              flex: 0.9,
              minWidth: 180,
              valueFormatter: (value: string | null | undefined) => getDisplayedUserName(value || undefined),
              renderCell: ({ value }: { value?: string | null }) => (
                <UserIdentity userId={value} usersById={responseUsersById} />
              ),
            },
          ]
        : []),
      {
        ...commonColumnSettings,
        field: 'userId',
        headerName: 'Submitted By',
        flex: 0.9,
        minWidth: 180,
        valueFormatter: (value, row) => row?.submittedBy || getDisplayedUserName(value),
        renderCell: ({ value, row }) => (
          <UserIdentity userId={value} displayName={row.submittedBy} usersById={responseUsersById} />
        ),
      },
      {
        ...commonColumnSettings,
        field: 'requestSender',
        headerName: 'Source',
        flex: 0.7,
        minWidth: 130,
        valueFormatter: (value) => requestSenderToDisplayText[value] || humanize(value),
      },
      {
        field: 'actions',
        filterable: false,
        type: 'actions',
        sortable: false,
        editable: false,
        cellClassName: 'actions',
        align: 'left',
        // Fixed width sized for all action slots so the DataGrid never needs to shrink this
        // column (which would otherwise collapse empty/reserved slots before real ones, since an
        // empty slot has no min-content and shrinks first - the exact bug this column previously had).
        width: ACTION_SLOT_SIZE * ACTION_SLOTS_COUNT,
        getActions: (params) => {
          const type = params.row.type;
          const externalTaskLink = params.row.externalTaskLink;
          const status = params.row.status;
          const jobId = params.row.id;
          const userId = params.row.userId;
          const isCancelableIngestJob =
            type === JobType.Ingest && (status === JobStatus.Pending || status === JobStatus.Running);
          const isCancelableExternalSlideTransferJob =
            type === JobType.ExternalSlideTransfer && (status === JobStatus.Pending || status === JobStatus.Running);
          const isCancelableClearMlJob = status === JobStatus.Running && Boolean(externalTaskLink);

          const isSecondaryAnalysisRebuild =
            params.row.type === JobType.PostProcessingAndCalculateFeatures && isSecondaryAnalysisJob(params.row);

          const prepareJobId = params.row.id;
          const prepareJobStudyId = params.row.studyId;
          const canShowContinue =
            archiveState === JobArchiveState.Active &&
            canContinueJob &&
            prepareJobId &&
            prepareJobStudyId &&
            params.row.type === JobType.PrepareCustomerResults &&
            (params.row.isParentJob === true || !params.row.parentId) &&
            !isLoadingPermissions;

          const canCancelJob = canCancelAllUsersJobs || (canCancelOwnJobs && userId === loggedInUserId);

          return [
            renderActionSlot(
              `externalTaskLink-${params.id}`,
              externalTaskLink ? (
                <Tooltip placement="top-start" title="Go to External Job Monitor (AWS)">
                  <Link
                    color="inherit"
                    underline="hover"
                    href={externalTaskLink}
                    target="_blank"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <IconButton>
                      <OpenInNewIcon />
                    </IconButton>
                  </Link>
                </Tooltip>
              ) : null
            ),
            renderActionSlot(
              `rebuild-${params.id}`,
              archiveState === JobArchiveState.Active && isRerunnableJob(params.row) ? (
                <Tooltip placement="top" title="Rebuild">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentJobModal?.({
                        jobType: type,
                        jobData: params.row,
                        ...(isSecondaryAnalysisRebuild
                          ? { additionalParams: { isSecondaryAnalysisRebuild: true } }
                          : {}),
                      });
                    }}
                  >
                    <RestartAltIcon />
                  </IconButton>
                </Tooltip>
              ) : null
            ),
            renderActionSlot(
              `continue-${params.id}`,
              canShowContinue ? (
                <Tooltip placement="top-start" title="Continue">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      const normalizedPrepareJobParams = normalizeKeysToCamelCase(params.row.params || {});
                      prepareCustomerResultsMutation.mutate({
                        jobId: prepareJobId!,
                        studyId: prepareJobStudyId!,
                        resultTypesToExport: get(normalizedPrepareJobParams, 'resultTypesToExport'),
                        queryObject: get(normalizedPrepareJobParams, 'queryObject'),
                        labId,
                        zipGranularity: get(normalizedPrepareJobParams, 'zipGranularity'),
                        generateFeaturesCsvFile: get(normalizedPrepareJobParams, 'generateFeaturesCsvFile'),
                        publishResultsToCustomer: get(normalizedPrepareJobParams, 'publishResultsToCustomer'),
                        unitsPerZip: get(normalizedPrepareJobParams, 'unitsPerZip'),
                        shardLimit: get(normalizedPrepareJobParams, 'shardLimit'),
                      });
                    }}
                  >
                    <ArrowRightIcon />
                  </IconButton>
                </Tooltip>
              ) : null
            ),
            renderActionSlot(
              `archive-${params.id}`,
              jobId ? <JobArchiveControl job={{ ...params.row, id: jobId }} /> : null
            ),
            renderActionSlot(
              `cancel-${params.id}`,
              hasCancelJobPermission ? (
                <Tooltip placement="top-start" title="Cancel job">
                  <span>
                    <IconButton
                      aria-label="Cancel job"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (jobId) {
                          cancelJobMutation.mutate(jobId);
                        }
                      }}
                      disabled={
                        !(isCancelableIngestJob || isCancelableExternalSlideTransferJob || isCancelableClearMlJob) ||
                        !canCancelJob ||
                        cancelJobMutation.isLoading
                      }
                    >
                      {cancelJobMutation.isLoading && cancelJobMutation.variables === jobId ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CloseIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null
            ),
          ];
        },
      },
    ];
  }, [
    usersById,
    responseUsersById,
    getUserNameById,
    uiSettings?.enumDisplayNames,
    archiveState,
    canContinueJob,
    hasCancelJobPermission,
    loggedInUserId,
    prepareCustomerResultsMutation,
    cancelJobMutation,
    setCurrentJobModal,
  ]);

  return {
    jobColumns: filter(jobColumns, (column) => !viewOnly || column.field !== 'actions'),
    isLoading: (canQueryUsers && !responseUsersById && isLoadingUsers) || isLoadingPermissions || isLoadingUiSettings,
  };
};
